<template>
  <div ref="wrapper" class="relative h-full w-full overflow-hidden" v-if="isParallaxActive">
    <!-- Optional layout spacer to preserve height -->
    <div v-if="aspectRatio && shouldShowSpacer" :style="{ paddingTop: aspectRatio }" />

    <!-- Parallax transform container -->
    <div
      ref="imageWrapper"
      class="absolute inset-0 will-change-transform"
      :style="{ transform: `translateY(${translateY}px)` }"
    >
      <div ref="slotWrapper" :class="props.wrapperClass">
        <slot />
      </div>
    </div>
  </div>
  <template v-else><slot /></template>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue'
import { useEventListener, useWindowScroll, useWindowSize } from '@vueuse/core'

const props = defineProps({
  noSpacer: {
    type: Boolean,
    default: false,
  },
  speed: {
    type: Number,
    default: 0.5, // 0 = fixed, 1 = scrolls with page
  },
  wrapperClass: {
    type: [String, Array, Object],
    default: '',
  },
  disabledOnMobile: {
    type: Boolean,
    default: true,
  },
  field_parallax_effect: {
    type: Boolean,
    default: false,
  },
})

const wrapper = ref<HTMLElement | null>(null)
const slotWrapper = ref<HTMLElement | null>(null)

const { y: scrollY } = useWindowScroll()
const { height: viewportHeight, width } = useWindowSize()

const offsetTop = ref(0)
const height = ref(0)
const aspectRatio = ref<string>('')

useEventListener(window, 'scroll', updateScroll)
useEventListener(window, 'resize', updateScroll)

const isMobile = computed(() => width.value < 768)

const isParallaxActive = computed(() => {
  return props.field_parallax_effect && !(props.disabledOnMobile && isMobile.value)
})

onMounted(async () => {
  await nextTick()
  calculateAspectRatio()
  updateScroll()
})

const shouldShowSpacer = computed(() => {
  const notReady = offsetTop.value === 0 && height.value === 0
  const tooCloseToTop = offsetTop.value < 150
  return !props.noSpacer && !(tooCloseToTop && !notReady)
})

// Parallax translate from center
const translateY = computed(() => {
  const isNearTop = offsetTop.value < 150

  if (isNearTop) {
    return scrollY.value * (1 - props.speed)
  }

  // Centered parallax — smooth start from middle
  const centerScroll = scrollY.value + viewportHeight.value / 2
  const centerOffset = centerScroll - (offsetTop.value + height.value / 2)
  return centerOffset * (1 - props.speed)
})

// Get scroll + offset
function updateScroll() {
  if (wrapper.value) {
    const rect = wrapper.value.getBoundingClientRect()
    offsetTop.value = window.scrollY + rect.top
    height.value = rect.height
  }
}

// Calculate aspect ratio from slot <img>
function calculateAspectRatio() {
  if (!slotWrapper.value) return
  const img = slotWrapper.value.querySelector('img')

  const applyRatioAndUpdate = () => {
    const ratio = (img!.naturalHeight / img!.naturalWidth) * 100
    aspectRatio.value = `${ratio.toFixed(2)}%`

    // Force next frame before measuring
    requestAnimationFrame(() => {
      updateScroll()
    })
  }

  if (img && img.complete && img.naturalWidth && img.naturalHeight) {
    applyRatioAndUpdate()
  } else if (img) {
    img.onload = () => applyRatioAndUpdate()
  }
}
</script>

<style scoped>
/* Ensure image fills parent */
::v-deep(img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}
</style>
